import React, { useEffect, useState } from 'react'
import { Activity, CheckCircle2, AlertTriangle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { markRecomputeExpected } from '@/lib/expectedRecompute'
import type { SyncStatus } from '@/types/shared'

// ─── Warm-up progress (onboarding) ────────────────────────────────────────────
//
// A just-onboarded property runs a background warm-up chain:
//   FetchHistoricalBookingsJob  → history_fetch_started / history_fetch_completed
//   BackfillHistoricalBookingsJob → backfill_completed
//   ComputeRatesJob (push: false) → computation_enqueued/_started → rates_computed
//
// This card narrates that chain in operator vocabulary as three stages. Events
// arrive via the PropertyNotificationsChannel fan-out that usePropertyNotifications
// re-dispatches on window as `crown:sync-status-updated` — no second cable
// subscription is opened here.
//
// Stage state is a monotonic FLOOR (0..3): events can only advance it, so
// out-of-order or replayed events never walk progress backwards, and an operator
// who opens the page mid-warm-up is fast-forwarded by whichever event lands next.

export interface WarmupStage {
  key: string
  label: string
  detail: string
}

export const WARMUP_STAGES: WarmupStage[] = [
  {
    key: 'import',
    label: 'Importing booking history',
    detail: 'The last 12 months of bookings, straight from your PMS',
  },
  {
    key: 'build',
    label: 'Building first preview rates',
    detail: 'Turning your booking history into a pricing foundation',
  },
  {
    key: 'refine',
    label: 'Refining with demand data',
    detail: 'Fine-tuning each date by how demand is shaping up',
  },
]

export type WarmupStageStatus = 'complete' | 'active' | 'pending' | 'error'

export interface WarmupState {
  // 0 = importing, 1 = building, 2 = refining, 3 = all complete
  floor: number
  errored: boolean
}

// Each broadcast event guarantees progress has reached AT LEAST this floor.
const EVENT_FLOOR: Record<string, number> = {
  history_fetch_started: 0,
  history_fetch_completed: 1,
  backfill_completed: 2,
  computation_enqueued: 2,
  computation_started: 2,
  rates_computed: 3,
}

// A compute starting again (retry/cron) or rates landing means a previous snag
// is no longer blocking — the card returns to its calm in-progress state.
const ERROR_CLEARING_EVENTS = new Set(['computation_enqueued', 'computation_started', 'rates_computed'])

// If events never arrive (channel hiccup, page opened mid-warm-up or days later),
// props are the fallback truth: a recorded computation means the whole warm-up
// chain already ran; an unresolved sync error means the warm-up hit a snag.
export function deriveInitialWarmupState(syncStatus: SyncStatus | null | undefined): WarmupState {
  if (syncStatus?.lastComputation) return { floor: 3, errored: false }
  return {
    // lastBookingsPolledAt is stamped by the first forward-window poll, so its
    // presence only confirms stage one is underway — which floor 0 already shows.
    floor: 0,
    errored: Boolean(syncStatus?.health?.errorMessage),
  }
}

export function applyWarmupEvent(state: WarmupState, event: string): WarmupState {
  if (event === 'computation_error') return { ...state, errored: true }

  const eventFloor = EVENT_FLOOR[event]
  if (eventFloor === undefined) return state

  return {
    floor: Math.max(state.floor, eventFloor),
    errored: ERROR_CLEARING_EVENTS.has(event) ? false : state.errored,
  }
}

export function stageStatus(state: WarmupState, index: number): WarmupStageStatus {
  if (index < state.floor) return 'complete'
  if (index === state.floor && state.floor < WARMUP_STAGES.length) {
    return state.errored ? 'error' : 'active'
  }
  return 'pending'
}

// Keep the "a recompute is expected" mark alive only while this card is mounted:
// when rates_computed fires, usePropertyNotifications then silently reloads the
// page (fresh props flip GoLiveBanner to "Preview rates ready" and the calendar
// cells fill in) instead of showing the manual "New rates available" banner.
// The short window + interval refresh means the mark dies soon after unmount.
const EXPECT_WINDOW_MS = 60_000
const EXPECT_REFRESH_MS = 25_000

const STAGE_PANEL_CLASSES: Record<WarmupStageStatus, string> = {
  complete: 'border-emerald-200/70 bg-white/70',
  active: 'border-teal-200 bg-white shadow-sm',
  pending: 'border-border/50 bg-white/40',
  error: 'border-amber-200 bg-amber-50/60',
}

const STAGE_LABEL_CLASSES: Record<WarmupStageStatus, string> = {
  complete: 'text-sm font-medium text-foreground',
  active: 'text-sm font-semibold text-teal-900',
  pending: 'text-sm font-medium text-muted-foreground',
  error: 'text-sm font-semibold text-amber-900',
}

const STAGE_DETAIL_CLASSES: Record<WarmupStageStatus, string> = {
  complete: 'text-muted-foreground',
  active: 'text-teal-700/80',
  pending: 'text-muted-foreground/70',
  error: 'text-amber-700/80',
}

function StageGlyph({ status }: { status: WarmupStageStatus }) {
  if (status === 'complete') return <CheckCircle2 className="h-4 w-4 text-emerald-600" />
  if (status === 'error') return <AlertTriangle className="h-4 w-4 text-amber-500" />
  if (status === 'active') {
    return (
      <span className="relative flex h-2.5 w-2.5">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-teal-400 opacity-60" />
        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-teal-500" />
      </span>
    )
  }
  return <span className="h-2.5 w-2.5 rounded-full border-2 border-slate-300" />
}

interface WarmupProgressProps {
  brand: string
  syncStatus?: SyncStatus | null
}

export default function WarmupProgress({ brand, syncStatus }: WarmupProgressProps) {
  const [state, setState] = useState<WarmupState>(() => deriveInitialWarmupState(syncStatus))

  useEffect(() => {
    markRecomputeExpected(EXPECT_WINDOW_MS)
    const id = setInterval(() => markRecomputeExpected(EXPECT_WINDOW_MS), EXPECT_REFRESH_MS)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    const handle = (e: Event) => {
      const detail = (e as CustomEvent).detail as { event?: string } | undefined
      const event = detail?.event
      if (!event) return
      setState((prev) => applyWarmupEvent(prev, event))
    }
    window.addEventListener('crown:sync-status-updated', handle)
    return () => window.removeEventListener('crown:sync-status-updated', handle)
  }, [])

  const allComplete = state.floor >= WARMUP_STAGES.length
  const statuses = WARMUP_STAGES.map((_, i) => stageStatus(state, i))
  // Display-only progress feel: completed stages plus a nudge for the one in
  // flight. Not rate math — no monetary values are involved here.
  const progressPct = ((state.floor + (allComplete || state.errored ? 0 : 0.35)) / WARMUP_STAGES.length) * 100

  const activeStage = WARMUP_STAGES[state.floor]
  const srMessage = allComplete
    ? 'Warm-up complete. Loading your preview rates.'
    : `Warm-up in progress: ${activeStage?.label ?? ''}`

  return (
    <div
      className="animate-slide-in rounded-xl border border-teal-200/70 bg-gradient-to-r from-teal-50 via-teal-50/70 to-slate-50 p-5 shadow-sm"
      role="status"
      aria-live="polite"
    >
      <span className="sr-only">{srMessage}</span>

      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-teal-100">
          <Activity className="h-5 w-5 text-teal-600" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-teal-900">Preparing your preview rates</h3>
          <p className="mt-0.5 text-sm text-teal-700">
            {allComplete
              ? 'All done — loading your preview rates onto the calendar…'
              : `${brand} is doing its homework on this property. Nothing is sent to your PMS, and there's no need to refresh — this page updates on its own.`}
          </p>
        </div>
      </div>

      {/* Progress track — fills as stages complete */}
      <div className="mt-4 h-1 overflow-hidden rounded-full bg-teal-100/80">
        <div
          className="h-full rounded-full bg-teal-500 transition-all duration-700 ease-out"
          style={{ width: `${progressPct}%` }}
        />
      </div>

      <ol className="mt-4 grid gap-2 sm:grid-cols-3 sm:gap-3">
        {WARMUP_STAGES.map((stage, i) => {
          const status = statuses[i]
          return (
            <li
              key={stage.key}
              data-status={status}
              className={cn(
                'flex items-start gap-2.5 rounded-lg border px-3 py-2.5 transition-colors duration-500',
                STAGE_PANEL_CLASSES[status]
              )}
            >
              <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
                <StageGlyph status={status} />
              </span>
              <div className="min-w-0">
                <p className={STAGE_LABEL_CLASSES[status]}>{stage.label}</p>
                <p className={cn('mt-0.5 text-xs', STAGE_DETAIL_CLASSES[status])}>{stage.detail}</p>
              </div>
            </li>
          )
        })}
      </ol>

      {state.errored && (
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200/80 bg-amber-50/80 px-3 py-2.5">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
          <p className="text-xs text-amber-800">
            We hit a snag while preparing your rates. {brand} will retry automatically — no action
            needed, and your calendar will fill in as soon as it succeeds.
          </p>
        </div>
      )}
    </div>
  )
}
